import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';
import { downloadMtrBulkBatch } from '@/integrations/mtrManager/client';
import type { MtrBulkScope } from '@/hooks/useCollectsApiData';
import { useEnsureMtrBulkFilter } from '@/hooks/useMtrBulkFilter';
import type { FilterState } from '@/hooks/useFilters';

/**
 * Baixa lotes de MTRs (ZIP) pelo mtr-manager, com o filtro resolvido no agreements. Um download por vez;
 * o progresso (lotes baixados) é local e não persiste entre sessões.
 *
 * `totalMtrs`/`batchSize` (do resumo — `MtrBulkDownloadSummary`) são opcionais e usados
 * apenas para calcular o `mtr_count` do evento `mtr_download` (RN-13): o último lote pode
 * ter menos MTRs que `batchSize`.
 *
 * GR-712: `mtr_download_solicitado` dispara no clique, antes de saber o resultado —
 * dá visibilidade de demanda mesmo quando o backend de geração do ZIP falha ou demora
 * (observado em staging). `mtr_download` continua exigindo sucesso confirmado (RN-06);
 * os dois eventos não são substitutos um do outro.
 */
export function useMtrBulkDownload(
  filters: FilterState,
  scope: MtrBulkScope = 'historico',
  totalMtrs?: number,
  batchSize?: number,
) {
  const { trackMtrDownload, trackButtonClick } = useAnalytics();
  const ensureBulkFilter = useEnsureMtrBulkFilter(filters, scope);

  const [isDownloading, setIsDownloading] = useState(false);
  const [activeBatch, setActiveBatch] = useState<number | null>(null);
  const [downloadedBatches, setDownloadedBatches] = useState<Set<number>>(
    new Set(),
  );

  const downloadBatch = useCallback(
    async (batch: number) => {
      if (isDownloading) return;
      setIsDownloading(true);
      setActiveBatch(batch);
      // Dispara já aqui, não no sucesso — não depende do backend responder.
      trackButtonClick({ buttonLabel: 'mtr_download_solicitado' });
      const toastId = toast.loading(`Gerando arquivo do lote ${batch + 1}...`);

      try {
        const { filter: bulkFilter, establishmentNames } = await ensureBulkFilter();
        const blob = await downloadMtrBulkBatch(bulkFilter, batch, establishmentNames);

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `mtrs-lote-${batch + 1}.zip`;
        anchor.click();
        window.URL.revokeObjectURL(url);

        setDownloadedBatches((prev) => new Set(prev).add(batch));
        toast.success(`Lote ${batch + 1} baixado com sucesso.`, { id: toastId });

        // GR-712: o download é sempre registrado. `mtrCount` é omitido quando o
        // resumo do lote (chamada separada) não carregou — antes disso o evento
        // simplesmente não disparava, subcontando downloads.
        const hasCount =
          typeof totalMtrs === 'number' &&
          typeof batchSize === 'number' &&
          batchSize > 0;
        trackMtrDownload(
          hasCount
            ? { mtrCount: Math.min(batchSize, totalMtrs - batch * batchSize) }
            : {},
        );
      } catch (error) {
        const message =
          error instanceof DOMException && error.name === 'AbortError'
            ? 'Tempo excedido ao gerar o arquivo. Tente novamente.'
            : error instanceof Error
              ? error.message
              : 'Não foi possível gerar o arquivo. Tente novamente.';
        toast.error(message, { id: toastId });
      } finally {
        setIsDownloading(false);
        setActiveBatch(null);
      }
    },
    [ensureBulkFilter, isDownloading, totalMtrs, batchSize, trackMtrDownload, trackButtonClick],
  );

  const resetProgress = useCallback(() => {
    setDownloadedBatches(new Set());
    setActiveBatch(null);
  }, []);

  return { isDownloading, activeBatch, downloadedBatches, downloadBatch, resetProgress };
}
